import React from 'react';
import { useDispatch } from 'react-redux';
import { Divider, makeStyles, Theme, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { PosPlugin } from 'plugins/plugin';

import { PrintingCountRules } from '../features/receiptDuplication/duplicateReceiptIfFirstPrint';
import { TgfPluginMeta } from '../meta';
import { getPluginConfiguration } from 'reducers/Plugins';
import { RootState } from 'reducers';
import { addError, addSuccess } from 'actions/Error';
import { ReceiptFooters } from './ReceiptFooters';

export interface ReceiptFooter {
  id: string;
  footerText: string;
  productGroupIds: string[];
}

export type Config = {
  printingCountRules?: PrintingCountRules;
  receiptFooters?: ReceiptFooter[];
};

export function getTgfConfiguration(state: RootState) {
  return getPluginConfiguration<Config>('tgf-customizations')(state);
}

const useStyles = makeStyles<Theme>(theme => ({
  configuration: { marginTop: '1rem' },
  alert: { marginBottom: '1rem' },
  code: { whiteSpace: 'pre', fontFamily: 'monospace' },
  jsonContainer: {
    background: '#00000025',
    padding: '1rem',
    maxHeight: '430px', // enough to fit default config
    overflow: 'auto',
    borderRadius: theme.shape.borderRadius,
  },
  divider: { margin: '1rem 0' },
}));

export const Configuration: Required<
  PosPlugin<Config>
>['ComponentConfigurationByLevel']['Company'] = ({ current, save }) => {
  const styles = useStyles();
  const dispatch = useDispatch();

  async function saveConfig(newConfig: Config) {
    try {
      await save(newConfig);
      dispatch(addSuccess('Configuration saved'));
    } catch (error) {
      dispatch(addError('Failed to save configuration'));
    }
  }

  return (
    <div className={styles.configuration}>
      <section>
        <Typography variant="h6" paragraph>
          Receipt duplication
        </Typography>
        <Alert severity="info" className={styles.alert}>
          No configuration UI is available for receipt duplication. Please
          configure the plugin manually or contact support
        </Alert>
        <Typography>
          Plugin ID: <span className={styles.code}>{TgfPluginMeta.id}</span>
        </Typography>
        <Typography paragraph>Current configuration:</Typography>
        <div className={styles.jsonContainer}>
          <Typography className={styles.code}>
            {JSON.stringify(
              { printingCountRules: current?.printingCountRules },
              undefined,
              4,
            )}
          </Typography>
        </div>
      </section>
      <Divider className={styles.divider} />
      <section>
        <ReceiptFooters
          footers={current?.receiptFooters}
          saveFooters={footers =>
            saveConfig({
              ...current,
              receiptFooters: footers,
            })
          }
        />
      </section>
    </div>
  );
};
